import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import BackLink from "@/components/BackLink";
import { EYEBROW, fieldClass, labelClass, primaryBtn, formatErr, GoldDivider } from "@/lib/authForm";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [resetPath, setResetPath] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
            setSent(true);
            if (data.reset_path) setResetPath(data.reset_path);
            toast.success("Check your email for reset instructions");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    const copyLink = async () => {
        const url = `${window.location.origin}${resetPath}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Reset link copied");
        } catch {
            toast.error("Could not copy link");
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif] flex items-center justify-center px-6 py-12">
            <div className="relative w-full max-w-[440px]">
                <BackLink to="/login" className="mb-5">Back to sign in</BackLink>

                <div className="mb-7 text-center">
                    <Link to="/" className="inline-block transition-opacity hover:opacity-90">
                        <BrandLogo
                            size={36}
                            textClass="font-serif text-2xl font-semibold text-[#4A0E17]"
                            accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C84B31]"
                        />
                    </Link>
                    <p className={`${EYEBROW} mt-6`} style={{ color: "#C9A227" }}>Account recovery</p>
                    <h1 className="mt-2 font-serif text-[2rem] font-semibold tracking-tight text-[#4A0E17]">
                        Forgot password
                    </h1>
                    <GoldDivider className="mx-auto mt-4" />
                </div>

                {!sent ? (
                    <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
                        <p className="text-center text-sm font-medium text-[#8A8178]">
                            Enter your account email and we&apos;ll send you a link to reset your password.
                        </p>
                        <div className="space-y-2">
                            <Label htmlFor="email" className={labelClass}>Email address</Label>
                            <div className="relative">
                                <Mail className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className={fieldClass}
                                    placeholder="you@yoursalon.com"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                        </div>
                        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="forgot-submit">
                            {busy ? "Sending…" : "Send reset link"}
                        </Button>
                    </form>
                ) : (
                    <div className="space-y-4 text-center" data-testid="forgot-password-sent">
                        <p className="text-sm font-medium text-[#5C534C]">
                            If an account exists for <strong>{email}</strong>, you will receive reset instructions shortly.
                        </p>
                        {resetPath && (
                            <div className="rounded-xl border border-[#D4AF37]/35 bg-[#FDF3DC]/45 p-4 text-left">
                                <p className="text-xs font-semibold text-[#8A8178]">
                                    Email is not configured — use this reset link:
                                </p>
                                <code className="mt-2 block truncate text-xs text-[#3D2817]">{resetPath}</code>
                                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={copyLink}>
                                    <Copy className="mr-1.5 size-3.5" /> Copy full link
                                </Button>
                            </div>
                        )}
                        <Link to="/login" className="inline-block text-sm font-semibold text-[#C84B31] hover:underline">
                            Return to sign in
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
